import { useState, useEffect } from 'react'
import { getNatal, saveBirthData, geocodeCity, generateNatal } from '../utils/api'

const C = {
  bg:        '#05050d',
  surface:   'rgba(255,255,255,0.028)',
  surface2:  'rgba(255,255,255,0.04)',
  border:    'rgba(180,185,230,0.09)',
  borderHi:  'rgba(180,185,230,0.2)',
  accent:    '#b8bdd4',
  accentDim: 'rgba(180,185,230,0.12)',
  text:      '#dce0f0',
  textMuted: '#8890aa',
  silverHi:  '#dce0f0',
  silverLo:  '#8890aa',
  green:     '#4dc88a',
  greenDim:  'rgba(77,200,138,0.1)',
  greenBorder:'rgba(77,200,138,0.25)',
  red:       '#d45a5a',
  redDim:    'rgba(212,90,90,0.1)',
  redBorder: 'rgba(212,90,90,0.25)',
  purple:    '#9b7fd4',
  orange:    '#e09858',
}
const font = {
  heading: "'Cormorant Garamond', serif",
  mono:    "'Space Mono', monospace",
  body:    "'Inter', sans-serif",
}

const PLANETS = [
  { key: 'sun',        label: 'Солнце ☉',       color: '#e8c84a' },
  { key: 'moon',       label: 'Луна ☽',          color: '#c8d8e8' },
  { key: 'rising',     label: 'Асцендент ↑',     color: C.accent },
  { key: 'mercury',    label: 'Меркурий ☿',      color: '#7ab8a8' },
  { key: 'venus',      label: 'Венера ♀',        color: '#d4a0b8' },
  { key: 'mars',       label: 'Марс ♂',          color: '#d46060' },
  { key: 'jupiter',    label: 'Юпитер ♃',        color: '#c8a050' },
  { key: 'saturn',     label: 'Сатурн ♄',        color: '#8898a8' },
  { key: 'north_node', label: 'Северный Узел ☊', color: C.purple },
]

const COMPARE_BLOCKS = [
  { key: 'personality',   label: 'Личность' },
  { key: 'values',        label: 'Ценности' },
  { key: 'career',        label: 'Карьера' },
  { key: 'communication', label: 'Стиль общения' },
  { key: 'strengths',     label: 'Сильные стороны' },
]

function useIsMobile(bp = 640) {
  const [m, setM] = useState(() => window.innerWidth < bp)
  useEffect(() => {
    let t
    const h = () => { clearTimeout(t); t = setTimeout(() => setM(window.innerWidth < bp), 80) }
    window.addEventListener('resize', h)
    return () => { clearTimeout(t); window.removeEventListener('resize', h) }
  }, [bp])
  return m
}

function ScoreBar({ score }) {
  const color = score >= 75 ? C.green : score >= 55 ? C.accent : C.orange
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ flex: 1, height: 4, background: C.border, borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${score}%`, background: color, borderRadius: 2, transition: 'width 0.6s ease' }} />
      </div>
      <span style={{ fontFamily: font.mono, fontSize: 11, color, minWidth: 32 }}>{score}%</span>
    </div>
  )
}

function MatchTag({ text, match }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 10px', borderRadius: 20, marginRight: 4, marginBottom: 4,
      fontFamily: font.body, fontSize: 11,
      background: match ? C.greenDim : C.redDim,
      border: `1px solid ${match ? C.greenBorder : C.redBorder}`,
      color: match ? C.green : C.red,
    }}>
      {match ? '✓' : '✗'} {text}
    </span>
  )
}

function GlassPanel({ children, style }) {
  return (
    <div style={{
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: 12,
      padding: '18px 20px',
      ...style,
    }}>
      {children}
    </div>
  )
}

function SectionLabel({ children }) {
  return (
    <div style={{
      fontFamily: font.mono, fontSize: 9, color: C.silverLo,
      letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 16,
    }}>
      {children}
    </div>
  )
}

// ─── Форма данных рождения ────────────────────────────────────────────────────

function BirthForm({ userId, initialData, onSaved }) {
  const isMobile = useIsMobile()
  const [date, setDate] = useState(initialData?.date || '')
  const [time, setTime] = useState(initialData?.time || '')
  const [city, setCity] = useState(initialData?.city || '')
  const [lat, setLat]   = useState(initialData?.lat || null)
  const [lon, setLon]   = useState(initialData?.lon || null)
  const [geocoding, setGeocoding] = useState(false)
  const [geoError, setGeoError]   = useState(null)
  const [saving, setSaving]       = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [triedSave, setTriedSave] = useState(false)

  async function handleGeocode() {
    if (!city.trim()) return
    setGeocoding(true)
    setGeoError(null)
    try {
      const res = await geocodeCity(city)
      if (res.lat) {
        setLat(res.lat)
        setLon(res.lon)
      } else {
        setGeoError('Город не найден. Попробуй другое написание.')
      }
    } catch {
      setGeoError('Не удалось связаться с сервером. Попробуй ещё раз.')
    } finally {
      setGeocoding(false)
    }
  }

  const missing = []
  if (!date) missing.push('дату рождения')
  if (!time) missing.push('время рождения (часы и минуты)')
  if (!city.trim()) missing.push('город рождения')

  async function handleSave() {
    setTriedSave(true)
    setSaveError(null)
    if (missing.length) return
    setSaving(true)
    try {
      let saveLat = lat, saveLon = lon
      if (!saveLat) {
        const res = await geocodeCity(city)
        if (res.lat) { saveLat = res.lat; saveLon = res.lon; setLat(res.lat); setLon(res.lon) }
      }
      const r = await saveBirthData(userId, { date, time, city, lat: saveLat, lon: saveLon })
      if (!r?.ok) throw new Error(r?.error || r?.detail || 'сервер не принял данные')
      onSaved({ date, time, city, lat: saveLat, lon: saveLon })
    } catch (e) {
      setSaveError('Не удалось сохранить: ' + (e.message || 'ошибка сети') + '. Попробуй ещё раз.')
    } finally {
      setSaving(false)
    }
  }

  const inp = {
    background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 8,
    padding: '8px 12px', color: C.text, fontFamily: font.body, fontSize: 13,
    outline: 'none', width: '100%', boxSizing: 'border-box',
  }
  const fieldStyle = (empty) => ({
    ...inp,
    border: `1px solid ${triedSave && empty ? C.red : C.border}`,
  })

  return (
    <GlassPanel style={{ marginBottom: 20 }}>
      <SectionLabel>Данные рождения</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          <div style={{ fontFamily: font.body, fontSize: 11, color: C.textMuted, marginBottom: 4 }}>Дата рождения</div>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} style={fieldStyle(!date)} />
        </div>
        <div>
          <div style={{ fontFamily: font.body, fontSize: 11, color: C.textMuted, marginBottom: 4 }}>Время рождения</div>
          <input type="time" value={time} onChange={e => setTime(e.target.value)} style={fieldStyle(!time)} />
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontFamily: font.body, fontSize: 11, color: C.textMuted, marginBottom: 4 }}>Город рождения</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="text" value={city} placeholder="Например: Москва"
            onChange={e => { setCity(e.target.value); setLat(null); setLon(null) }}
            style={fieldStyle(!city.trim())}
          />
          <button onClick={handleGeocode} disabled={geocoding || !city.trim()} style={{
            padding: '8px 14px', borderRadius: 8, cursor: 'pointer', whiteSpace: 'nowrap',
            fontFamily: font.body, fontSize: 12,
            background: C.accentDim, border: `1px solid ${C.borderHi}`, color: C.accent,
            opacity: (!city.trim() || geocoding) ? 0.5 : 1,
          }}>
            {geocoding ? '...' : 'Найти'}
          </button>
        </div>
        {geoError && <div style={{ fontSize: 12, color: C.red, marginTop: 4 }}>{geoError}</div>}
        {lat && <div style={{ fontSize: 11, color: C.green, marginTop: 4 }}>✓ {city} — {lat.toFixed(2)}°, {lon.toFixed(2)}°</div>}
      </div>
      {triedSave && missing.length > 0 && (
        <div style={{ fontSize: 12, color: C.red, marginBottom: 10, fontFamily: font.body }}>
          Не заполнено: {missing.join(', ')}.
        </div>
      )}
      {saveError && (
        <div style={{ fontSize: 12, color: C.red, marginBottom: 10, fontFamily: font.body }}>{saveError}</div>
      )}
      <button onClick={handleSave} disabled={saving} style={{
        padding: '8px 20px', borderRadius: 20, cursor: saving ? 'default' : 'pointer',
        fontFamily: font.body, fontSize: 13, fontWeight: 500,
        background: C.accentDim, border: `1px solid ${C.accent}`, color: C.accent,
        opacity: saving ? 0.5 : 1,
      }}>
        {saving ? 'Сохраняю...' : 'Сохранить'}
      </button>
    </GlassPanel>
  )
}

// ─── Основной компонент ───────────────────────────────────────────────────────

export default function NatalChart({ userId = 'user', isOwner = false }) {
  const [data, setData]             = useState(null)
  const [birthData, setBirthData]   = useState(null)
  const [loading, setLoading]       = useState(true)
  const [generating, setGenerating] = useState(false)
  const [error, setError]           = useState(null)
  const [showBirthForm, setShowBirthForm] = useState(false)
  const [forecastTab, setForecastTab] = useState('month')
  const isMobile = useIsMobile()

  useEffect(() => { loadNatal() }, [userId])

  async function loadNatal() {
    setLoading(true)
    try {
      const d = await getNatal(userId)
      if (d?.planets) setData(d)
      if (d?.birth_data) setBirthData(d.birth_data)
    } catch {}
    setLoading(false)
  }

  async function handleGenerate() {
    setGenerating(true)
    setError(null)
    try {
      const d = await generateNatal(userId)
      if (d.error) throw new Error(d.error)
      setData(d)
    } catch (e) {
      setError(e.message)
    }
    setGenerating(false)
  }

  function handleBirthSaved(bd) {
    setBirthData(bd)
    setShowBirthForm(false)
  }

  const hasBirth = birthData?.date && birthData?.time && birthData?.city

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 20px' }}>
        <div style={{ fontFamily: font.mono, fontSize: 11, color: C.textMuted, letterSpacing: '0.1em' }}>
          загрузка...
        </div>
      </div>
    )
  }

  // ─── Пустое состояние ────────────────────────────────────────────────────────
  if (!data) {
    return (
      <div style={{ paddingBottom: 48 }}>
        <div style={{ textAlign: 'center', padding: '40px 20px 32px' }}>
          <div style={{ fontFamily: font.mono, fontSize: 9, color: C.silverLo, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 16 }}>
            Натальная карта
          </div>
          <h2 style={{ fontFamily: font.heading, fontSize: 36, fontWeight: 300, color: C.accent, margin: '0 0 8px' }}>
            Астрологический профиль
          </h2>
          <p style={{ fontFamily: font.body, fontSize: 13, color: C.textMuted, maxWidth: 420, margin: '0 auto 28px', lineHeight: 1.7 }}>
            Введи данные рождения — Swiss Ephemeris рассчитает точные позиции планет,
            Claude сравнит их с твоим реальным психологическим профилем.
          </p>
          {error && <p style={{ color: C.red, fontSize: 13, marginBottom: 16 }}>{error}</p>}
        </div>

        <BirthForm
          userId={userId}
          initialData={birthData}
          onSaved={handleBirthSaved}
        />

        {hasBirth && (
          <div style={{ textAlign: 'center' }}>
            <button onClick={handleGenerate} disabled={generating} style={{
              padding: '10px 28px', borderRadius: 24, cursor: 'pointer',
              fontFamily: font.body, fontSize: 14, fontWeight: 500,
              background: C.accentDim, border: `1px solid ${C.accent}`, color: C.accent,
              opacity: generating ? 0.6 : 1,
            }}>
              {generating ? 'Рассчитываю...' : 'Рассчитать карту'}
            </button>
          </div>
        )}
      </div>
    )
  }

  // ─── Карта загружена ─────────────────────────────────────────────────────────
  const { planets = {}, comparison = {}, forecast = {} } = data

  const forecastTabs = [
    { key: 'week',  label: 'Неделя' },
    { key: 'month', label: 'Месяц' },
    { key: 'year',  label: 'Год' },
  ]

  return (
    <div style={{ paddingBottom: 48 }}>

      {/* Заголовок */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <div style={{ fontFamily: font.mono, fontSize: 9, color: C.silverLo, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 6 }}>
            Натальная карта
          </div>
          <h2 style={{ fontFamily: font.heading, fontSize: 28, fontWeight: 300, color: C.accent, margin: '0 0 4px' }}>
            {birthData?.city || ''}
          </h2>
          <p style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, margin: 0 }}>
            {birthData?.date} · {birthData?.time} · Совпадение с профилем:{' '}
            <span style={{ color: comparison.overall >= 70 ? C.green : C.accent, fontWeight: 600 }}>
              {comparison.overall}%
            </span>
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={() => setShowBirthForm(v => !v)} style={{
            padding: '5px 14px', borderRadius: 20, cursor: 'pointer',
            fontFamily: font.body, fontSize: 11,
            background: 'transparent', border: `1px solid ${C.border}`, color: C.textMuted,
          }}>
            {showBirthForm ? 'Скрыть' : 'Данные'}
          </button>
          <button onClick={handleGenerate} disabled={generating} style={{
            padding: '5px 14px', borderRadius: 20, cursor: 'pointer',
            fontFamily: font.body, fontSize: 11,
            background: 'transparent', border: `1px solid ${C.border}`, color: C.textMuted,
            opacity: generating ? 0.5 : 1,
          }}>
            {generating ? '...' : 'Пересчитать'}
          </button>
        </div>
      </div>

      {error && <div style={{ marginBottom: 16, padding: '10px 14px', background: C.redDim, border: `1px solid ${C.redBorder}`, borderRadius: 8, color: C.red, fontSize: 13 }}>{error}</div>}

      {showBirthForm && (
        <BirthForm userId={userId} initialData={birthData} onSaved={handleBirthSaved} />
      )}

      {/* Три главных планеты */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
        {['sun', 'moon', 'rising'].map(key => {
          const planet = PLANETS.find(p => p.key === key)
          const d = planets[key] || {}
          return (
            <div key={key} style={{
              background: C.surface, border: `1px solid ${C.border}`,
              borderRadius: 12, padding: '16px 20px',
              borderTop: `2px solid ${planet.color}`,
            }}>
              <div style={{ fontFamily: font.mono, fontSize: 9, color: planet.color, marginBottom: 4, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                {planet.label}
              </div>
              <div style={{ fontFamily: font.heading, fontSize: 26, fontWeight: 300, color: C.silverHi, marginBottom: 2 }}>
                {d.sign || '—'}
                {d.degree != null && (
                  <span style={{ fontFamily: font.mono, fontSize: 11, color: C.textMuted, marginLeft: 8 }}>{d.degree}°</span>
                )}
              </div>
              <p style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, margin: '0 0 10px', lineHeight: 1.6 }}>
                {d.interpretation || ''}
              </p>
              {(d.matches?.length > 0 || d.differences?.length > 0) && (
                <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 8, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {d.matches?.map((m, i) => <MatchTag key={`m${i}`} text={m} match={true} />)}
                  {d.differences?.map((dif, i) => <MatchTag key={`d${i}`} text={dif} match={false} />)}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Остальные планеты */}
      <GlassPanel style={{ marginBottom: 16 }}>
        <SectionLabel>Планеты</SectionLabel>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
          {['mercury', 'venus', 'mars', 'jupiter', 'saturn', 'north_node'].map(key => {
            const planet = PLANETS.find(p => p.key === key)
            const d = planets[key] || {}
            return (
              <div key={key} style={{
                padding: '12px 14px',
                background: C.surface2,
                border: `1px solid ${C.border}`,
                borderLeft: `2px solid ${planet.color}`,
                borderRadius: 8,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontFamily: font.mono, fontSize: 9, color: planet.color, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    {planet.label}
                  </span>
                  <span style={{ fontFamily: font.heading, fontSize: 16, color: C.text }}>
                    {d.sign || '—'}
                    {d.degree != null && (
                      <span style={{ fontFamily: font.mono, fontSize: 10, color: C.textMuted, marginLeft: 6 }}>{d.degree}°</span>
                    )}
                  </span>
                </div>
                <p style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, margin: '0 0 8px', lineHeight: 1.5 }}>
                  {d.interpretation || ''}
                </p>
                {(d.matches?.length > 0 || d.differences?.length > 0) && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {d.matches?.map((m, i) => <MatchTag key={`m${i}`} text={m} match={true} />)}
                    {d.differences?.map((dif, i) => <MatchTag key={`d${i}`} text={dif} match={false} />)}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </GlassPanel>

      {/* Сравнение по блокам */}
      <GlassPanel style={{ marginBottom: 16 }}>
        <SectionLabel>Натальная карта vs Реальный профиль</SectionLabel>
        {COMPARE_BLOCKS.map(block => {
          const cmp = comparison[block.key] || {}
          return (
            <div key={block.key} style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontFamily: font.body, fontSize: 13, color: C.text }}>{block.label}</span>
              </div>
              <ScoreBar score={cmp.score || 0} />

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10, marginTop: 10 }}>
                <div style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ fontFamily: font.mono, fontSize: 9, color: C.purple, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>
                    ✦ По звёздам
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                    {(cmp.natal || []).map((item, i) => (
                      <span key={i} style={{
                        display: 'inline-block', padding: '2px 10px', borderRadius: 20, marginRight: 4, marginBottom: 4,
                        background: 'rgba(155,127,212,0.1)', border: '1px solid rgba(155,127,212,0.25)',
                        fontFamily: font.body, fontSize: 11, color: C.purple,
                      }}>{item}</span>
                    ))}
                  </div>
                </div>
                <div style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ fontFamily: font.mono, fontSize: 9, color: C.green, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>
                    ● По данным
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                    {(cmp.real || []).map((item, i) => (
                      <span key={i} style={{
                        display: 'inline-block', padding: '2px 10px', borderRadius: 20, marginRight: 4, marginBottom: 4,
                        background: C.greenDim, border: `1px solid ${C.greenBorder}`,
                        fontFamily: font.body, fontSize: 11, color: C.green,
                      }}>{item}</span>
                    ))}
                  </div>
                </div>
              </div>

              {(cmp.matches?.length > 0 || cmp.differences?.length > 0) && (
                <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {(cmp.matches || []).map((m, i) => <MatchTag key={`m${i}`} text={m} match={true} />)}
                  {(cmp.differences || []).map((d, i) => <MatchTag key={`d${i}`} text={d} match={false} />)}
                </div>
              )}
            </div>
          )
        })}
      </GlassPanel>

      {/* Прогноз */}
      <GlassPanel>
        <SectionLabel>Астрологический прогноз</SectionLabel>
        <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
          {forecastTabs.map(t => (
            <button key={t.key} onClick={() => setForecastTab(t.key)} style={{
              padding: '5px 18px', borderRadius: 20, cursor: 'pointer',
              fontFamily: font.body, fontSize: 12,
              background: forecastTab === t.key ? C.accentDim : 'transparent',
              border: `1px solid ${forecastTab === t.key ? C.accent : C.border}`,
              color: forecastTab === t.key ? C.accent : C.textMuted,
            }}>
              {t.label}
            </button>
          ))}
        </div>

        {forecast[forecastTab] && (() => {
          const f = forecast[forecastTab]
          return (
            <div>
              {f.theme && (
                <div style={{
                  background: C.accentDim, border: `1px solid rgba(180,185,230,0.2)`,
                  borderRadius: 8, padding: '12px 16px', marginBottom: 16,
                }}>
                  <div style={{ fontFamily: font.mono, fontSize: 9, color: C.accent, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>
                    Тема периода
                  </div>
                  <p style={{ fontFamily: font.body, fontSize: 13, color: C.text, margin: 0, lineHeight: 1.6 }}>{f.theme}</p>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
                {f.opportunities?.length > 0 && (
                  <div>
                    <div style={{ fontFamily: font.mono, fontSize: 9, color: C.green, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>
                      ↑ Возможности
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {f.opportunities.map((o, i) => (
                        <li key={i} style={{ fontFamily: font.body, fontSize: 13, color: C.text, marginBottom: 6, lineHeight: 1.5 }}>{o}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {f.watch_out && (
                  <div>
                    <div style={{ fontFamily: font.mono, fontSize: 9, color: C.orange, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>
                      ⚠ Будь внимателен
                    </div>
                    <p style={{ fontFamily: font.body, fontSize: 13, color: C.textMuted, margin: 0, lineHeight: 1.6 }}>{f.watch_out}</p>
                  </div>
                )}
              </div>
              {f.energy && (
                <div style={{ marginTop: 14, fontFamily: font.body, fontSize: 12, color: C.textMuted, fontStyle: 'italic' }}>{f.energy}</div>
              )}
            </div>
          )
        })()}
      </GlassPanel>
    </div>
  )
}
